<template>
  <div>
    <p class="help mb-2">{{ $t('conflict.table_cells_differ') }}</p>
    <div class="columns">
      <div class="column conflict-column">
        <label class="label">{{ $t('conflict.yours') }}</label>
        <div class="table-wrapper">
          <table class="conflict-table" data-testid="conflict-table-local">
            <thead>
              <tr>
                <th
                  v-for="(header, colIndex) in headersLocal"
                  :key="'local-header-' + colIndex"
                >
                  <span class="cell-content" :title="header">
                    {{ header || ' ' }}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, rowIndex) in dataRowsLocal"
                :key="'local-row-' + rowIndex"
              >
                <td
                  v-for="(cell, colIndex) in row"
                  :key="'local-cell-' + rowIndex + '-' + colIndex"
                  :class="{
                    'conflict-cell': isConflictCell(rowIndex + 1, colIndex),
                  }"
                >
                  <span class="cell-content" :title="cell">{{ cell }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="column conflict-column">
        <label class="label">{{ $t('conflict.theirs') }}</label>
        <div class="table-wrapper">
          <table class="conflict-table" data-testid="conflict-table-server">
            <thead>
              <tr>
                <th
                  v-for="(header, colIndex) in headersServer"
                  :key="'server-header-' + colIndex"
                >
                  <span class="cell-content" :title="header">
                    {{ header || ' ' }}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(row, rowIndex) in dataRowsServer"
                :key="'server-row-' + rowIndex"
              >
                <td
                  v-for="(cell, colIndex) in row"
                  :key="'server-cell-' + rowIndex + '-' + colIndex"
                  :class="{
                    'conflict-cell': isConflictCell(rowIndex + 1, colIndex),
                  }"
                >
                  <span class="cell-content" :title="cell">{{ cell }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  cellDisplayText,
  sameCellContent,
} from '../../../../helpers/tableCellMerge';

const toTextTable = (table) =>
  (Array.isArray(table) ? table : []).map((row) =>
    (Array.isArray(row) ? row : []).map((cell) => cellDisplayText(cell))
  );

export default {
  name: 'ConflictInstructionTableField',
  props: {
    local: { type: Array, default: () => [] },
    server: { type: Array, default: () => [] },
  },
  computed: {
    localText() {
      return toTextTable(this.local);
    },
    serverText() {
      return toTextTable(this.server);
    },
    headersLocal() {
      return this.localText[0] || [];
    },
    dataRowsLocal() {
      return this.localText.slice(1);
    },
    headersServer() {
      return this.serverText[0] || [];
    },
    dataRowsServer() {
      return this.serverText.slice(1);
    },
    conflictCells() {
      const cells = new Set();
      const rowCount = Math.max(this.local.length, this.server.length);
      for (let rowIndex = 0; rowIndex < rowCount; rowIndex += 1) {
        const localRow = this.local[rowIndex] || [];
        const serverRow = this.server[rowIndex] || [];
        const colCount = Math.max(localRow.length, serverRow.length);
        for (let colIndex = 0; colIndex < colCount; colIndex += 1) {
          if (!sameCellContent(localRow[colIndex], serverRow[colIndex])) {
            cells.add(`${rowIndex}:${colIndex}`);
          }
        }
      }
      return cells;
    },
  },
  methods: {
    isConflictCell(rowIndex, colIndex) {
      return this.conflictCells.has(`${rowIndex}:${colIndex}`);
    },
  },
};
</script>

<style scoped>
.column.conflict-column {
  width: 50%;
  overflow-x: auto;
}

.table-wrapper {
  width: 100%;
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow-x: auto;
}

.conflict-table {
  width: max-content; /* allows cells to grow up to their content size */
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.85rem;
  background: white;
}

.conflict-table th,
.conflict-table td {
  border: 1px solid #ccc;
  padding: 4px 8px;
  text-align: center;
  min-width: 100px; /* each cell has enough room */
  max-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.conflict-table td.conflict-cell {
  background: #fff3cd;
  font-weight: 600;
}

.cell-content {
  display: block;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
